document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

const revealSelectors = [
  '#MainContent > .shopify-section:not(:first-child)',
  '#MainContent > section:not(:first-child)',
  '.main-collection__header',
  '.main-collection__grid > *',
  '.main-list-collections__header',
  '.lg-collection-card',
  '.main-search__head',
  '.search-tools',
  '.search-grid > *',
  '.lg-pagination'
];
const revealSelector = revealSelectors.join(',');
const staggerSelector = '.main-collection__grid > *, .search-grid > *, .lg-collection-card';
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const canReveal = !reduceMotion && 'IntersectionObserver' in window;
let revealObserver;

if (canReveal) {
  revealObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-revealed');
      observer.unobserve(entry.target);
    });
  }, { threshold: 0.08, rootMargin: '0px 0px -8% 0px' });
}

const registerRevealTargets = (root = document) => {
  if (!canReveal || !revealObserver) return;

  const targets = [];
  if (root instanceof Element && root.matches(revealSelector)) targets.push(root);
  targets.push(...root.querySelectorAll(revealSelector));

  targets.forEach((target, index) => {
    if (target.dataset.lgRevealRegistered === 'true') return;
    target.dataset.lgRevealRegistered = 'true';
    target.classList.add('lg-reveal');
    if (target.matches(staggerSelector)) {
      target.style.setProperty('--lg-reveal-delay', `${(index % 4) * 70}ms`);
    }
    revealObserver.observe(target);
  });
};

registerRevealTargets();

document.addEventListener('shopify:section:load', (event) => {
  registerRevealTargets(event.target);
});

const header = document.querySelector('[data-site-header]');
const syncHeader = () => header?.classList.toggle('is-scrolled', window.scrollY > 40);
syncHeader();
window.addEventListener('scroll', syncHeader, { passive: true });

document.addEventListener('click', (event) => {
  const mediaButton = event.target.closest('[data-product-media-direction]');
  if (mediaButton) {
    event.preventDefault();
    event.stopPropagation();

    const card = mediaButton.closest('[data-product-card]');
    const images = Array.from(card?.querySelectorAll('[data-product-media]') || []);

    if (card && images.length > 1) {
      const activeIndex = images.findIndex((image) => image.classList.contains('is-active'));
      const currentIndex = activeIndex >= 0 ? activeIndex : 0;
      const step = mediaButton.dataset.productMediaDirection === 'previous' ? -1 : 1;
      const nextIndex = (currentIndex + step + images.length) % images.length;

      images.forEach((image, index) => image.classList.toggle('is-active', index === nextIndex));
      card.classList.add('is-interacting');
    }
  }

  const menuButton = event.target.closest('[data-menu-toggle]');
  if (menuButton) {
    const open = document.body.classList.toggle('menu-open');
    menuButton.setAttribute('aria-expanded', String(open));
    document.querySelector('[data-mobile-menu]')?.setAttribute('aria-hidden', String(!open));
  }

  const tab = event.target.closest('[data-tab-target]');
  if (tab) {
    const section = tab.closest('[data-tabs]');
    section?.querySelectorAll('[data-tab-target]').forEach((item) => {
      const active = item === tab;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-selected', String(active));
    });
    section?.querySelectorAll('[data-tab-panel]').forEach((panel) => {
      const active = panel.dataset.tabPanel === tab.dataset.tabTarget;
      panel.classList.toggle('is-active', active);
      panel.hidden = !active;
    });
  }

  const storyThumb = event.target.closest('[data-story-target]');
  if (storyThumb) {
    const section = storyThumb.closest('[data-stories]');
    section?.querySelectorAll('[data-story-target]').forEach((item) => {
      const active = item === storyThumb;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-pressed', String(active));
    });
    section?.querySelectorAll('[data-story-panel]').forEach((panel) => panel.classList.toggle('is-active', panel.dataset.storyPanel === storyThumb.dataset.storyTarget));
  }
});

document.addEventListener('pointerout', (event) => {
  const card = event.target.closest?.('[data-product-card]');
  if (!card || card.contains(event.relatedTarget)) return;

  const images = card.querySelectorAll('[data-product-media]');
  images.forEach((image, index) => image.classList.toggle('is-active', index === 0));
  card.classList.remove('is-interacting');
});

/* Product variants */
const initProductSection = (section) => {
  if (!section || section.dataset.productReady === 'true') return;
  const variantsElement = section.querySelector('[data-product-variants]');
  const form = section.querySelector('form[action*="/cart/add"]');
  if (!variantsElement || !form) return;

  let variants;
  try { variants = JSON.parse(variantsElement.textContent); } catch (error) { return; }
  section.dataset.productReady = 'true';
  const optionGroups = Array.from(section.querySelectorAll('[data-product-option]'));
  const idInput = form.querySelector('[data-variant-id]');
  const button = form.querySelector('[data-add-to-bag]');
  const stickyBar = section.querySelector('[data-product-sticky]');
  const stickyPrice = section.querySelector('[data-product-sticky-price]');
  const stickyButton = section.querySelector('[data-sticky-submit]');
  if (stickyBar && button && 'IntersectionObserver' in window) {
    const stickyObserver = new IntersectionObserver(([entry]) => stickyBar.classList.toggle('is-visible', !entry.isIntersecting), { threshold: 0.1 });
    stickyObserver.observe(button);
  }
  const price = section.querySelector('[data-product-price]');
  const mediaItems = Array.from(section.querySelectorAll('[data-product-media-id]'));

  const showMedia = (mediaId) => {
    if (!mediaId || !mediaItems.some((media) => Number(media.dataset.productMediaId) === Number(mediaId))) return;
    mediaItems.forEach((media) => {
      const active = Number(media.dataset.productMediaId) === Number(mediaId);
      media.classList.toggle('is-active', active);
      // All product photos remain visible in the editorial gallery.
    });
    section.querySelectorAll('[data-product-gallery-target]').forEach((thumbnail) => {
      const active = Number(thumbnail.dataset.productGalleryTarget) === Number(mediaId);
      thumbnail.classList.toggle('is-active', active);
      thumbnail.setAttribute('aria-current', String(active));
    });
  };

  const selectedOptions = () => optionGroups.map((group) => {
    const radio = group.querySelector('[data-option-value]:checked');
    return (radio || group.querySelector('[data-option-value]'))?.value;
  });

  const updateSizeAvailability = (selected) => {
    optionGroups.forEach((group, groupIndex) => {
      if (!group.classList.contains('product-option--size')) return;
      group.querySelectorAll('[data-option-value]').forEach((input) => {
        const available = variants.some((candidate) => candidate.available && candidate.options[groupIndex] === input.value && candidate.options.every((value, index) => index === groupIndex || value === selected[index]));
        input.disabled = !available;
        const label = input.nextElementSibling;
        if (!label || label.tagName !== 'LABEL') return;
        label.classList.toggle('is-unavailable', !available);
        label.setAttribute('aria-disabled', String(!available));
        const note = label.querySelector('[data-option-sold-out-note]');
        if (note) note.hidden = available;
      });
    });
  };

  const updateVariant = () => {
    const selected = selectedOptions();
    updateSizeAvailability(selected);
    const variant = variants.find((item) => item.options.every((value, index) => value === selected[index]));
    if (!variant) {
      if (button) { button.disabled = true; button.textContent = 'Unavailable'; }
      if (stickyButton) { stickyButton.disabled = true; stickyButton.textContent = 'Unavailable'; }
      return;
    }

    idInput.value = variant.id;
    if (button) {
      button.disabled = !variant.available;
      button.textContent = variant.available ? 'Add to bag' : 'Sold out';
    }
    if (price) price.innerHTML = `${variant.price}${variant.comparePrice ? `<span class="compare-price">${variant.comparePrice}</span>` : ''}`;
    if (stickyPrice) stickyPrice.textContent = variant.price;
    if (stickyButton) { stickyButton.disabled = !variant.available; stickyButton.textContent = variant.available ? 'Add to bag' : 'Sold out'; }

    showMedia(variant.mediaId);
    if (variant.url && window.history?.replaceState) window.history.replaceState({}, '', variant.url);

    optionGroups.forEach((group, groupIndex) => {
      const selectedLabel = group.querySelector('[data-selected-option]');
      if (selectedLabel) selectedLabel.textContent = selected[groupIndex];
    });
  };

  optionGroups.forEach((group) => group.addEventListener('change', updateVariant));
  section.addEventListener('click', (event) => {
    const stickySubmit = event.target.closest('[data-sticky-submit]');
    if (stickySubmit) {
      event.preventDefault();
      if (!stickySubmit.disabled) form.requestSubmit();
      return;
    }
    const thumbnail = event.target.closest('[data-product-gallery-target]');
    if (thumbnail) {
      showMedia(thumbnail.dataset.productGalleryTarget);
      return;
    }

    const arrow = event.target.closest('[data-product-gallery-direction]');
    if (arrow && mediaItems.length > 1) {
      const currentIndex = Math.max(0, mediaItems.findIndex((media) => media.classList.contains('is-active')));
      const step = arrow.dataset.productGalleryDirection === 'previous' ? -1 : 1;
      const next = mediaItems[(currentIndex + step + mediaItems.length) % mediaItems.length];
      showMedia(next.dataset.productMediaId);
      return;
    }

    const quantityButton = event.target.closest('[data-quantity-step]');
    if (quantityButton) {
      const input = form.querySelector('input[name="quantity"]');
      if (!input) return;
      const minimum = Number(input.min) || 1;
      input.value = Math.max(minimum, (Number(input.value) || minimum) + Number(quantityButton.dataset.quantityStep));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });
  updateVariant();
};

document.querySelectorAll('[data-product-section]').forEach(initProductSection);
document.addEventListener('shopify:section:load', (event) => initProductSection(event.target.querySelector('[data-product-section]')));

/* AJAX bag drawer */
const cartDrawer = document.querySelector('[data-cart-drawer]');
let cartReturnFocus = null;
const currency = window.Shopify?.currency?.active || 'PHP';
const formatMoney = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(cents / 100);
const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[character]));

const openCartDrawer = (source) => {
  if (!cartDrawer) return;
  cartReturnFocus = source || document.activeElement;
  cartDrawer.classList.add('is-open');
  cartDrawer.setAttribute('aria-hidden', 'false');
  document.body.classList.add('cart-drawer-open');
  cartDrawer.querySelector('.lg-cart-drawer__panel')?.focus();
};

const closeCartDrawer = () => {
  if (!cartDrawer) return;
  cartDrawer.classList.remove('is-open');
  cartDrawer.setAttribute('aria-hidden', 'true');
  document.body.classList.remove('cart-drawer-open');
  cartReturnFocus?.focus?.();
};

const renderCart = (cart) => {
  if (!cartDrawer) return;
  document.querySelectorAll('[data-cart-count]').forEach((count) => { count.textContent = `(${cart.item_count})`; });
  const items = cartDrawer.querySelector('[data-cart-items]');
  const footer = cartDrawer.querySelector('[data-cart-footer]');
  const total = cartDrawer.querySelector('[data-cart-total]');
  if (total) total.textContent = formatMoney(cart.total_price);
  if (footer) footer.hidden = cart.item_count === 0;
  if (!items) return;

  if (!cart.items.length) {
    items.innerHTML = '<div class="lg-cart-drawer__empty"><p>Your bag is waiting.</p><button type="button" class="text-link" data-cart-close>Continue shopping</button></div>';
    return;
  }

  items.innerHTML = cart.items.map((item) => {
    const image = item.featured_image?.url || item.image;
    const imageUrl = image ? `${image}${image.includes('?') ? '&' : '?'}width=240` : '';
    const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<p>${escapeHtml(item.variant_title)}</p>` : '';
    return `<article class="lg-cart-item" data-cart-line data-key="${escapeHtml(item.key)}">
      <a class="lg-cart-item__image" href="${escapeHtml(item.url)}">${imageUrl ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(item.product_title)}" width="120" height="160" loading="lazy">` : ''}</a>
      <div class="lg-cart-item__details"><h3><a href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a></h3>${variant}<p>${formatMoney(item.final_line_price)}</p>
      <div class="lg-cart-item__actions"><label>Qty <input type="number" min="0" value="${item.quantity}" data-cart-quantity aria-label="Quantity for ${escapeHtml(item.product_title)}"></label><button type="button" data-cart-remove>Remove</button></div></div>
    </article>`;
  }).join('');
};

const getCart = async () => {
  const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error('Unable to load bag');
  return response.json();
};

document.addEventListener('submit', async (event) => {
  const form = event.target.closest?.('form[action*="/cart/add"]');
  if (!form || event.defaultPrevented) return;
  event.preventDefault();
  const submit = event.submitter || form.querySelector('[type="submit"]');
  const originalText = submit?.textContent;
  if (submit) { submit.disabled = true; submit.textContent = 'Adding…'; }
  try {
    const response = await fetch(`${form.action}.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
    if (!response.ok) { const error = await response.json(); throw new Error(error.description || 'Could not add this item'); }
    const cart = await getCart();
    renderCart(cart);
    openCartDrawer(submit);
  } catch (error) {
    const notice = cartDrawer?.querySelector('[data-cart-notice]');
    if (notice) notice.textContent = error.message;
    openCartDrawer(submit);
  } finally {
    if (submit) { submit.disabled = false; submit.textContent = originalText; }
  }
});

document.addEventListener('click', async (event) => {
  const openButton = event.target.closest('[data-cart-open]');
  if (openButton) {
    event.preventDefault();
    try { renderCart(await getCart()); } catch (error) { /* Use server-rendered bag if refresh fails. */ }
    openCartDrawer(openButton);
    return;
  }
  if (event.target.closest('[data-cart-close]')) { closeCartDrawer(); return; }
  const remove = event.target.closest('[data-cart-remove]');
  if (remove) {
    const line = remove.closest('[data-cart-line]');
    const input = line?.querySelector('[data-cart-quantity]');
    if (input) { input.value = 0; input.dispatchEvent(new Event('change', { bubbles: true })); }
  }
});

document.addEventListener('change', async (event) => {
  const input = event.target.closest?.('[data-cart-quantity]');
  if (!input) return;
  const line = input.closest('[data-cart-line]');
  input.disabled = true;
  try {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: line.dataset.key, quantity: Math.max(0, Number(input.value) || 0) }) });
    if (!response.ok) throw new Error('Could not update bag');
    renderCart(await response.json());
  } catch (error) {
    const notice = cartDrawer?.querySelector('[data-cart-notice]');
    if (notice) notice.textContent = error.message;
    input.disabled = false;
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && cartDrawer?.classList.contains('is-open')) closeCartDrawer();
});
